import React, { useState } from 'react';
import { addSize } from '../../services/api';
import colors from '../../theme/colors';

const ROOM_CATEGORIES = [
  'Living Room',
  'Kitchen',
  'Bathroom',
  'Outdoor',
  'Bedroom',
  'Commercial',
  'Elevation',
  'Counter Tops',
  'Step-Riser',
  'Waterline'
];

export default function AddSizeModal({ isOpen, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    name: '',
    width: '',
    height: '',
    unit: 'mm',
    default_layout: 'grid',
    room_categories: []
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRoomCategoryChange = (category) => {
    setFormData(prev => ({
      ...prev,
      room_categories: prev.room_categories.includes(category)
        ? prev.room_categories.filter(c => c !== category)
        : [...prev.room_categories, category]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    

    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        if (key === 'room_categories') {
          formData.room_categories.forEach(cat => {
            formDataToSend.append('room_categories', cat);
          });
        } else if (formData[key]) {
          formDataToSend.append(key, formData[key]);
        }
      });

      const response = await addSize(formDataToSend);
      if (response.success) {
        setFormData({
          name: '',
          width: '',
          height: '',
          unit: 'mm',
          default_layout: 'grid',
          room_categories: []
        });
        onSuccess && onSuccess();
        onClose();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto">
      <div 
        className="bg-white rounded-xl p-6 max-w-3xl w-full mx-4 my-8"
        style={{ backgroundColor: colors.surface }}
      >
        <div className="flex items-center justify-between mb-6">
          <h3 
            className="text-lg font-semibold"
            style={{ color: colors.t1 }}
          >
            Add Product Size
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
          <div className="grid grid-cols-2 gap-4">
            {/* Size Name */}
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-2" style={{ color: colors.t2 }}>
                Size Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="e.g., 600 x 1200 mm"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent outline-none"
              />
            </div>

            {/* Width */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: colors.t2 }}>
                Enter Product Width <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                step="0.01"
                name="width"
                value={formData.width}
                onChange={handleChange}
                required
                placeholder="Product width"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent outline-none"
              />
            </div>

            {/* Height */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: colors.t2 }}>
                Enter Product Height <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                step="0.01"
                name="height"
                value={formData.height}
                onChange={handleChange}
                required
                placeholder="Product height"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent outline-none"
              />
            </div>

            {/* Unit */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: colors.t2 }}>
                Select Unit <span className="text-red-500">*</span>
              </label>
              <select
                name="unit"
                value={formData.unit}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent outline-none"
              >
                <option value="mm">Millimeters (mm)</option>
                <option value="cm">Centimeters (cm)</option>
                <option value="inch">Inches</option>
                <option value="feet">Feet</option>
              </select>
            </div>

            {/* Default Layout */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: colors.t2 }}>
                Select default layout <span className="text-red-500">*</span>
              </label>
              <select
                name="default_layout"
                value={formData.default_layout}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent outline-none"
              >
                <option value="grid">Grid</option>
                <option value="brick">Brick</option>
                <option value="vertical">Vertical</option>
                <option value="horizontal">Horizontal</option>
                <option value="herringbone">Herringbone</option>
              </select>
            </div>

            {/* Room Categories */}
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-3" style={{ color: colors.t2 }}>
                Show/Hide Size By Room Category
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {ROOM_CATEGORIES.map((category) => (
                  <label key={category} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.room_categories.includes(category)}
                      onChange={() => handleRoomCategoryChange(category)}
                      className="w-4 h-4 text-accent border-gray-300 rounded focus:ring-accent"
                    />
                    <span className="text-sm" style={{ color: colors.t3 }}>{category}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="flex gap-3 justify-end pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              style={{ color: colors.t2 }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-white rounded-lg transition-colors disabled:cursor-not-allowed disabled:bg-gray-300"
              style={{ backgroundColor: colors.accent }}
              onMouseEnter={(e) => e.target.style.backgroundColor = colors.accent2}
              onMouseLeave={(e) => e.target.style.backgroundColor = colors.accent}
            >
              {loading ? 'Adding Size...' : 'Submit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
